import React from "react";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import {
  getSchemaTypeIcon,
  SchemaType,
} from "src/base-components/SchemaTypeSelector";
import { Select } from "src/base-components/Select";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import {
  getStatusOptions,
  STATUS_KEY_SEPARATOR,
} from "src/cases/common/statusOptions";
import { CasePropertiesValue } from "src/clients/case-management";
import { CaseCreationNodeMetaMappedValueValueTypeEnum } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

type StatusAssignmentCardProps = {
  fieldName: string;
  property: CasePropertiesValue;
  nodeId: string;
  status: string;
  subStatus?: string;
  valueType: CaseCreationNodeMetaMappedValueValueTypeEnum;
  onChangeValueType: (
    valueType: CaseCreationNodeMetaMappedValueValueTypeEnum,
  ) => void;
  onChangeValue: (status: string, subStatus?: string) => void;
  immutable: boolean;
  error?: boolean;
  enableSubStatus?: boolean;
};

export const StatusAssignmentCard: React.FC<StatusAssignmentCardProps> = ({
  nodeId,
  fieldName,
  property,
  status,
  subStatus,
  valueType,
  onChangeValueType,
  onChangeValue,
  immutable,
  error,
  enableSubStatus,
}) => {
  // Construct the current selected key from status and subStatus
  const currentKey = subStatus
    ? `${status}${STATUS_KEY_SEPARATOR}${subStatus}`
    : status;
  const isStatusHighlighted = useIsFieldHighlighted(`${nodeId}:status`);
  const isSubStatusHighlighted = useIsFieldHighlighted(`${nodeId}:sub_status`);

  return (
    <div>
      <Label>Status</Label>
      <SimpleRadioGroup
        className="space-x-4"
        disabled={immutable}
        orientation="horizontal"
        value={
          valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
            ? "fixed"
            : "dynamic"
        }
        onValueChange={(selectedValue) =>
          onChangeValueType(
            selectedValue === "fixed"
              ? CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED
              : CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC,
          )
        }
      >
        <SimpleRadioGroup.Item
          dataLoc={`case-creation-${fieldName}-fixed`}
          label="Use a predefined case property"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="fixed"
        />
        <SimpleRadioGroup.Item
          dataLoc={`case-creation-${fieldName}-dynamic`}
          label="Map to a specific data field"
          labelClassName="ml-1.5 font-inter-normal-13px"
          value="dynamic"
        />
      </SimpleRadioGroup>
      {valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.FIXED && (
        <div className="mt-5">
          <Select
            buttonRenderer={(option) => (
              <div data-loc={`case-creation-${fieldName}-select-value`}>
                {option?.value}
              </div>
            )}
            dataLoc={`case-creation-${fieldName}-select`}
            disabled={immutable}
            errored={error}
            options={getStatusOptions(property)}
            placeholder={`Select ${fieldName}`}
            value={currentKey}
            fullWidth
            onChange={(selectedKey) => {
              // Parse the key to extract status and subStatus
              const parts = selectedKey.split(STATUS_KEY_SEPARATOR);
              const [status, subStatus] = parts;

              onChangeValue(status, subStatus);
            }}
          />
        </div>
      )}
      {valueType === CaseCreationNodeMetaMappedValueValueTypeEnum.DYNAMIC && (
        <div className="mt-5 flex flex-col gap-y-2">
          <div className="flex w-full items-center gap-2">
            <div className="w-1/2">
              <AutocompleteCodeInput
                dataLoc={`case-creation-${fieldName}-value`}
                disabled={immutable}
                error={error}
                isSearchHighlighted={isStatusHighlighted}
                placeholder="data.field"
                value={status}
                onChange={(selectedValue) => {
                  onChangeValue(selectedValue, subStatus);
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Icon icon={faArrowRight} size="xs" />
            </div>
            <div className="w-1/2">
              <Input
                placeholder={property.display_name || fieldName}
                prefixIcon={{
                  icon: getSchemaTypeIcon(property.type as SchemaType),
                }}
                disabled
                fullWidth
              />
            </div>
          </div>

          {enableSubStatus && (
            <div className="flex w-full items-center gap-2">
              <div className="w-1/2">
                <AutocompleteCodeInput
                  dataLoc="case-creation-sub-status-value"
                  disabled={immutable}
                  error={error}
                  isSearchHighlighted={isSubStatusHighlighted}
                  placeholder="data.field"
                  value={subStatus || ""}
                  onChange={(selectedValue) => {
                    onChangeValue(status, selectedValue);
                  }}
                />
              </div>
              <div className="flex-shrink-0">
                <Icon icon={faArrowRight} size="xs" />
              </div>
              <div className="w-1/2">
                <Input
                  placeholder="Sub-status"
                  prefixIcon={{
                    icon: getSchemaTypeIcon(property.type as SchemaType),
                  }}
                  disabled
                  fullWidth
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
